/**
 * Doctors index page.
 *
 * Lists all clinic doctors with profiles, photos, specialties.
 * All current records are DEMO data and carry a visible notice.
 */

import Seo from "@/components/Seo";
import { useLanguage } from "@/contexts/LanguageContext";
import type { Locale } from "@/content/types";
import { DOCTORS } from "@/content/doctors";
import { clinicNode } from "@/lib/schema";
import { localizedPath } from "@/lib/locale";
import { motion } from "framer-motion";
import { AlertCircle } from "lucide-react";

interface DoctorsIndexPageProps {
  locale: Locale;
}

export default function DoctorsIndexPage({ locale }: DoctorsIndexPageProps) {
  const { t } = useLanguage();

  const title = locale === "ar" ? "أطبائنا" : "Our Team";
  const description =
    locale === "ar"
      ? "تعرّف على فريقنا من أطباء الأسنان المتخصصين الذين يقدمون رعاية عالية الجودة"
      : "Meet our team of specialist dentists dedicated to providing excellent patient care";

  const demoNoticeTitle =
    locale === "ar" ? "بيانات تجريبية" : "Demo Data";
  const demoNoticeText =
    locale === "ar"
      ? "ملفات الأطباء أدناه بيانات تجريبية مؤقتة للمراجعة. الملفات الحقيقية معتمدة من العيادة ستحل محلها قريبًا."
      : "The doctor profiles below are temporary demo data for review. Verified clinic-approved profiles will be coming soon.";

  return (
    <>
      <Seo
        title={title}
        description={description}
        path={`/${locale}/doctors`}
        locale={locale}
        schema={clinicNode(locale)}
      />
      <main className="container mx-auto px-4 py-12">
        <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }}>
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-white">{title}</h1>
          <p className="text-lg text-slate-300 mb-8 max-w-2xl">
            {description}
          </p>
        </motion.div>

        {/* DEMO Notice */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          whileInView={{ opacity: 1, y: 0 }}
          className="mb-12 p-4 rounded-lg border border-amber-600/50 bg-amber-900/20 flex gap-3 items-start"
        >
          <AlertCircle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
          <div>
            <h2 className="font-semibold text-amber-300 mb-1">{demoNoticeTitle}</h2>
            <p className="text-sm text-amber-200/90">{demoNoticeText}</p>
          </div>
        </motion.div>

        {/* Doctors Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {DOCTORS.map((doctor, idx) => (
            <motion.a
              key={doctor.slug}
              href={localizedPath(locale, `/doctors/${doctor.slug}`)}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.1 }}
              whileHover={{ y: -4 }}
              className="group block overflow-hidden rounded-xl border border-slate-700 bg-slate-900/50 hover:border-primary/50 hover:bg-slate-900/80 transition-all duration-300"
            >
              {/* Photo */}
              <div className="aspect-square overflow-hidden bg-slate-800">
                <img
                  src={doctor.photo.src}
                  alt={doctor.photo.alt[locale]}
                  width={doctor.photo.width}
                  height={doctor.photo.height}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>

              {/* Info */}
              <div className="p-6">
                <h3 className="font-semibold text-lg text-white mb-2">
                  {doctor.name[locale]}
                </h3>
                <p className="text-sm text-primary font-medium mb-3">
                  {doctor.specialty[locale]}
                </p>
                <p className="text-sm text-slate-400">
                  {locale === "ar" ? "اعرف أكثر ←" : "Learn more →"}
                </p>
              </div>
            </motion.a>
          ))}
        </div>
      </main>
    </>
  );
}
